import Vue from 'vue';
import { CameraOptions } from 'mapbox-gl';
import { TranslateResult } from 'vue-i18n';

import ListoryApi from './services/api';
import KeyValueDb from './services/db';
import PoiJournalService from './services/poi-journal';
import PoiMonitor from './services/poi-monitor';
import GeolocatorService from './services/geolocator';
import UnityBridge from './unity-bridge';

import { AppView } from './models/api';
import { Route } from './models/route';
import { Sponsor } from './models/sponsor';
import { Poi, PoiJournalEntry, PoiState } from './models/poi';
import { Settings } from './models/settings';
import { DefaultOnBoardingProgress, OnBoardingProgress, HomeContent, NewsEntry } from '@/models/home';
import { ArExperienceChoices, DefaultOnBoardingPoi } from './constants';
import { HomeHelper } from './services/home-helper';
import { Quiz, Answer } from './models/quiz';

export interface StoreContent {
    sponsors: Sponsor[];
    routes: Route[];
    pois: Poi[];
    poiJournal: PoiJournalEntry[];
    $t(key: string, values?: any): string | TranslateResult;

    onBoardingProgress: OnBoardingProgress;
    startedScenes: ArExperienceChoices[];
    saveOnBoardingProgress(): any;
}

export default class ListoryStore extends Vue implements StoreContent {
    // Services
    public api: ListoryApi;
    public db: KeyValueDb;
    public geolocator: GeolocatorService;
    public poiMonitor: PoiMonitor;
    public poiJournalService: PoiJournalService;
    public ub: UnityBridge;

    // Stored state
    public settings: Settings;
    public sponsors: Sponsor[];
    public routes: Route[];
    public pois: Poi[];
    public quiz: Quiz;
    public poiJournal: PoiJournalEntry[];
    public poiFavorites: number[];
    public onBoardingProgress: OnBoardingProgress;
    public arOnBoardingManualVisits: number;
    public startedScenes: ArExperienceChoices[];
    public quizAnswers: Answer[];

    // Temporary state
    public mapPosition: CameraOptions;
    public mapChangeSourceRequest: boolean;
    public mapShowFavorites: boolean;
    public homeContent: HomeContent = null;
    public newsEntry: NewsEntry = null;

    // Private stuff
    private loaded: boolean = false;
    private loadTask: Promise<boolean>;
    private recoverOnce: boolean;

    constructor(options?: any) {
        super(options);
        this.api = new ListoryApi();
        this.geolocator = new GeolocatorService(this);
        this.poiMonitor = new PoiMonitor(this);
        this.poiJournalService = new PoiJournalService(this);
        this.ub = new UnityBridge();
        this.ub.addMessageHandler('restoredDB', event => this.onDBRecovered(event));
        this.settings = new Settings();
    }

    public get isReady(): boolean {
        return this.loaded;
    }

    public async waitUntilReady() {
        if (this.loaded) {
            return;
        }

        if (this.loadTask !== undefined) {
            await this.loadTask;
        }
    }

    public async init(): Promise<boolean> {
        this.loadTask = this.internalUpdate();
        this.loaded = await this.loadTask;
        this.loadTask = undefined;

        this.poiMonitor.start();

        return this.loaded;
    }

    public async forceReload(): Promise<boolean> {
        this.newsEntry = null;

        this.loaded = false;

        this.loadTask = this.internalUpdate(true);
        this.loaded = await this.loadTask;
        this.loadTask = undefined;

        return this.loaded;
    }

    public getRoute(id: number): Route {
        return this.routes.find(route => route.id === id);
    }

    public getPoi(id: number): Poi {
        return this.pois.find(poi => poi.id === id);
    }

    public async saveState(): Promise<boolean> {
        try {
            await this.db.set('poi-journal', this.poiJournal);
            await this.db.set('settings', this.settings);
            await this.backupToUnity();

            return true;
        } catch (error) {
            console.error('Failed to save store state! ', error);
            return false;
        }
    }

    public async saveArOnBoardingManualVisits(): Promise<boolean> {
        try {
            await this.db.set('ar-on-boarding-manual-visits', { data: this.arOnBoardingManualVisits });
            return true;
        } catch (error) {
            console.error('Failed to save ar onBoarding visits count state! ', error);
            return false;
        }
    }

    public async saveOnBoardingProgress(): Promise<boolean> {
        try {
            await this.db.set('on-boarding-progress', { data: JSON.stringify(this.onBoardingProgress) });

            return true;
        } catch (error) {
            console.error('Failed to save onBoarding state! ', error);
            return false;
        }
    }

    public async saveFavoritePoiIds(): Promise<boolean> {
        try {
            await this.db.set('poi-favorites', this.poiFavorites);
            return true;
        } catch (error) {
            console.error('Failed to save favorite poi ids! ', error);
            return false;
        }
    }

    public async saveQuizAnswers(): Promise<boolean> {
        try {
            await this.db.set('quiz-answers', this.quizAnswers);
            return true;
        } catch (error) {
            console.error('Failed to save quiz answers! ', error);
            return false;
        }
    }

    public async saveSceneStarted(scene: ArExperienceChoices): Promise<boolean> {
        try {
            if (!this.startedScenes.includes(scene)) {
                this.startedScenes.push(scene);
            }
            await this.db.set('started-scenes', this.startedScenes);
            return true;
        } catch (error) {
            console.error('Failed to save opened scenes! ', error);
            return false;
        }
    }

    public async loadContent(): Promise<boolean> {
        // Reset content
        this.sponsors = [];
        this.routes = [];
        this.pois = [];
        this.quiz = null;
        this.poiJournal = [];
        this.onBoardingProgress = DefaultOnBoardingProgress;
        this.arOnBoardingManualVisits = 0;
        this.startedScenes = [];
        this.quizAnswers = [];

        // Load content from db
        this.settings = (await this.db.get('settings')) || new Settings();

        const content = (await this.db.get('content')) as AppView;
        if (content) {
            this.sponsors = content.sponsors;
            this.routes = content.routes;
            this.pois = content.pois;
            this.quiz = content.quiz;
        } else {
            if (!this.recoverOnce) {
                this.recoverFromUnity();
                this.recoverOnce = true;
            }
        }

        this.poiJournal = (await this.db.get('poi-journal')) || [];
        this.poiJournalService.restorePoiStates();

        // Unlock default onBoarding poi
        let saveState = await this.unlockOnboardingPoi();

        // Set AR Poi's to r2u "el grande feature"
        saveState = await this.unlockAllArPois();

        if (saveState) {
            await this.saveState();
        }

        this.poiFavorites = (await this.db.get('poi-favorites')) || [];

        const onBoardingProgress = await this.db.get('on-boarding-progress');
        if (onBoardingProgress) {
            this.onBoardingProgress = JSON.parse(onBoardingProgress.data);
        }

        this.arOnBoardingManualVisits = (await this.db.get('ar-on-boarding-manual-visits')) || 0;

        const startedScenes = await this.db.get('started-scenes');
        if (startedScenes) {
            this.startedScenes = startedScenes;
        }

        this.quizAnswers = (await this.db.get('quiz-answers')) || [];

        // create home content
        this.homeContent = HomeHelper.GetHomeContent(this);

        return true;
    }

    private async unlockOnboardingPoi(): Promise<boolean> {
        if (this.pois.length > 0 && this.poiJournal.length === 0) {
            const defaultPoi = this.getPoi(DefaultOnBoardingPoi);

            // fake poi state by auto unlocking it
            const entryR2u = await this.poiJournalService.createJournalEntry(defaultPoi, PoiState.READY_TO_UNLOCK);
            Vue.set(defaultPoi, 'state', PoiState.READY_TO_UNLOCK);
            this.poiJournal.push(entryR2u);
            await this.saveState();

            const entryU = await this.poiJournalService.createJournalEntry(defaultPoi, PoiState.UNLOCKED);
            Vue.set(defaultPoi, 'state', PoiState.UNLOCKED);
            this.poiJournal.push(entryU);
            return true;
        }

        return false;
    }

    private async unlockAllArPois(): Promise<boolean> {
        if (this.pois.length === 0) return false;

        let poiUnlocked = false;
        for (const arPoi of this.pois) {
            if (!arPoi.is_special) continue;

            // check whether the ar poi is inside the journal or not, when its not inside -> push it to r2u
            if (!this.poiJournal.some(pe => pe.poi_id === arPoi.id)) {
                // fake poi state by auto unlocking it
                const entryR2u = await this.poiJournalService.createJournalEntry(arPoi, PoiState.READY_TO_UNLOCK);
                Vue.set(arPoi, 'state', PoiState.READY_TO_UNLOCK);
                this.poiJournal.push(entryR2u);
                poiUnlocked = true;
            }
        }
        return poiUnlocked;
    }

    private async backupToUnity() {
        const dbData = await this.db.backup();
        this.ub.send('backupDatabase', dbData);
    }

    private recoverFromUnity() {
        this.ub.send('restoreDatabase');
    }

    private async onDBRecovered(event: any) {
        if (!this.db) return;
        if (event && event.detail && event.detail.length > 0) {
            try {
                await this.db.restore(event.detail);
                await this.loadContent();
            } catch (error) {
                // Ignored
            }
        }
    }

    private async internalUpdate(forceReload = false): Promise<boolean> {
        if (!this.db) {
            this.db = await KeyValueDb.connect();
        }

        // Initial load if content is available
        const hasContent = forceReload ? false : await this.loadContent();

        if (!navigator.onLine) {
            console.info('Offline, not checking if content is up to date');
            return hasContent;
        }

        const apiVersion = await this.api.getAppViewVersion(this.settings.language);
        // If we can't fetch updated api data, we have to survive with what we got
        if (!apiVersion) return true;

        const dbVersion = await this.db.get('content-version');
        const dbIsUpToDate = apiVersion === dbVersion;
        if (hasContent && dbIsUpToDate && !forceReload) {
            console.info('Content is up to date');
            return true;
        }

        console.info('Loading updated API data');
        const appView = await this.api.getAppView(this.settings.language);
        if (!appView) return hasContent;

        // appView.routes = connectRoutes(appView.routes);

        // Update stuff in DB
        await this.db.set('content', appView);
        await this.db.set('content-version', apiVersion);

        // Reload content
        await this.loadContent();

        this.$emit('storeUpdated');

        return true;
    }
}
